import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    ArcElement
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faChartBar,
    faChartPie,
    faCalendarAlt,
    faRupeeSign,
    faBox,
    faUsers,
    faTruck,
    faFileInvoice,
    faMoneyBillWave,
    faSpinner,
    faDownload,
    faFilter,
    faTimes,
    faCheckCircle,
    faExclamationCircle,
    faPrint,
    faClock,
    faStore,
    faIndustry,
    faPercentage,
    faCalculator,
    faReceipt,
    faWallet,
    faArrowUp,
    faArrowDown,
    faCircle,
    faSquare,
    faChevronLeft,
    faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import "../styles/ProfitLoss.css";

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    ArcElement
);

// ==============================================
// Translation Utility
// ==============================================
const plReportTranslator = () => {
    let cache = new Map();
    const defaultTexts = {
        profitLoss: 'Profit & Loss Report',
        financialDashboard: 'Financial Dashboard',
        totalRevenue: 'Total Revenue',
        totalExpenses: 'Total Expenses',
        netProfit: 'Net Profit',
        netMargin: 'Net Margin',
        salesOverview: 'Sales Overview',
        expenseBreakdown: 'Expense Breakdown',
        profitTrend: 'Profit Trend',
        dailyPerformance: 'Daily Performance',
        monthlyTrends: 'Monthly Trends',
        topExpenses: 'Top Expenses',
        customerReceivables: 'Customer Receivables',
        supplierPayables: 'Supplier Payables',
        costOfGoodsSold: 'Cost of Goods Sold',
        grossProfit: 'Gross Profit',
        salaries: 'Salaries',
        rawMaterials: 'Raw Materials',
        otherExpenses: 'Other Expenses',
        viewAll: 'View All',
        vsLastPeriod: 'vs last period',
        filterByDate: 'Filter by Date',
        today: 'Today',
        thisWeek: 'This Week',
        thisMonth: 'This Month',
        thisYear: 'This Year',
        custom: 'Custom',
        apply: 'Apply',
        reset: 'Reset',
        exportCSV: 'Export CSV',
        loading: 'Loading financial data...',
        noData: 'No data available for selected period',
        currency: 'Rs.',
        selectDateRange: 'Select Date Range',
        startDate: 'Start Date',
        endDate: 'End Date'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;
        return defaultTexts;
    };

    return { getTranslations };
};

const translator = plReportTranslator();

// ==============================================
// Toast Component
// ==============================================
const PlToast = ({ toast, setToast, t }) => {
    if (!toast.show) return null;
    
    const icons = {
        success: faCheckCircle,
        error: faExclamationCircle,
        warning: faExclamationCircle
    };
    
    return (
        <div className={`pl-toast pl-toast-${toast.type}`}>
            <div className="pl-toast-inner">
                <FontAwesomeIcon icon={icons[toast.type]} className="pl-toast-icon" />
                <div className="pl-toast-content">
                    <div className="pl-toast-title">{toast.type === 'success' ? 'Success' : 'Error'}</div>
                    <div className="pl-toast-message">{toast.message}</div>
                </div>
                <button className="pl-toast-close" onClick={() => setToast({ show: false })}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="pl-toast-progress" style={{ width: `${toast.progress}%` }} />
        </div>
    );
};

// ==============================================
// Metric Card Component
// ==============================================
const PlMetricCard = ({ title, value, subtitle, change, icon, color, loading, t }) => {
    const isPositive = change && change > 0;
    
    return (
        <div className="pl-metric-card" style={{ borderTopColor: color }}>
            <div className="pl-metric-header">
                <div className="pl-metric-icon" style={{ background: `${color}15`, color }}>
                    <FontAwesomeIcon icon={icon} />
                </div>
                <div className="pl-metric-title">{title}</div>
            </div>
            <div className="pl-metric-value">
                {loading ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="pl-spinner" />
                ) : (
                    <>
                        <span className="pl-currency">Rs.</span>
                        <span>{typeof value === 'number' ? value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : value}</span>
                    </>
                )}
            </div>
            {subtitle && <div className="pl-metric-subtitle">{subtitle}</div>}
            {change !== undefined && (
                <div className={`pl-metric-change ${isPositive ? 'positive' : 'negative'}`}>
                    <FontAwesomeIcon icon={isPositive ? faArrowUp : faArrowDown} />
                    <span>{Math.abs(change)}% {t?.vsLastPeriod}</span>
                </div>
            )}
        </div>
    );
};

// ==============================================
// Revenue vs Expenses Line Chart
// ==============================================
const PlRevenueExpenseChart = ({ data, loading, t }) => {
    const chartData = {
        labels: data?.map(d => d.date) || [],
        datasets: [
            {
                label: t?.totalRevenue || 'Revenue',
                data: data?.map(d => d.sales) || [],
                borderColor: '#10b981',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                fill: true,
                tension: 0.4,
                pointRadius: 3,
                pointHoverRadius: 6,
                pointBackgroundColor: '#10b981',
                pointBorderColor: '#fff',
                borderWidth: 2
            },
            {
                label: t?.totalExpenses || 'Expenses',
                data: data?.map(d => d.expenses) || [],
                borderColor: '#ef4444',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                fill: true,
                tension: 0.4,
                pointRadius: 3,
                pointHoverRadius: 6,
                pointBackgroundColor: '#ef4444',
                pointBorderColor: '#fff',
                borderWidth: 2
            },
            {
                label: t?.netProfit || 'Net Profit',
                data: data?.map(d => d.net_profit) || [],
                borderColor: '#8b5cf6',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                fill: true,
                tension: 0.4,
                pointRadius: 3,
                pointHoverRadius: 6,
                pointBackgroundColor: '#8b5cf6',
                pointBorderColor: '#fff',
                borderWidth: 2
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top', labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
            tooltip: { 
                mode: 'index', 
                intersect: false,
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: Rs.${ctx.raw.toLocaleString('en-IN')}`
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: { callback: (value) => `Rs.${value.toLocaleString('en-IN')}` }
            }
        }
    };

    if (loading) {
        return (
            <div className="pl-chart-placeholder">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>{t?.loading}</span>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="pl-chart-placeholder">
                <FontAwesomeIcon icon={faChartLine} />
                <span>{t?.noData}</span>
            </div>
        );
    }

    return (
        <div className="pl-chart-container">
            <Line data={chartData} options={options} height={280} />
        </div>
    );
};

// ==============================================
// Expense Pie Chart (Compact)
// ==============================================
const PlExpensePieChart = ({ data, loading, t }) => {
    const colors = ['#f59e0b', '#ef4444', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
    
    const chartData = {
        labels: data?.map(d => d.category) || [],
        datasets: [{
            data: data?.map(d => d.total_amount) || [],
            backgroundColor: colors,
            borderWidth: 0,
            hoverOffset: 10
        }]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, font: { size: 10 } } },
            tooltip: { 
                callbacks: {
                    label: (ctx) => `${ctx.label}: Rs.${ctx.raw.toLocaleString('en-IN')} (${((ctx.raw / ctx.dataset.data.reduce((a,b) => a + b, 0)) * 100).toFixed(1)}%)`
                }
            }
        }
    };

    if (loading) {
        return (
            <div className="pl-pie-placeholder">
                <FontAwesomeIcon icon={faSpinner} spin />
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="pl-pie-placeholder">
                <span>{t?.noData}</span>
            </div>
        );
    }

    return (
        <div className="pl-pie-container-compact">
            <Doughnut data={chartData} options={options} height={180} />
        </div>
    );
};

// ==============================================
// Profit Trend Bar Chart (Compact)
// ==============================================
const PlProfitTrendChart = ({ data, loading, t }) => {
    const chartData = {
        labels: data?.map(d => d.month_name) || [],
        datasets: [
            {
                label: t?.netProfit || 'Net Profit',
                data: data?.map(d => d.profit) || [],
                backgroundColor: data?.map(d => d.profit >= 0 ? 'rgba(16, 185, 129, 0.7)' : 'rgba(239, 68, 68, 0.7)'),
                borderRadius: 6,
                barPercentage: 0.7,
                categoryPercentage: 0.85
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { 
                callbacks: { 
                    label: (ctx) => `${ctx.dataset.label}: Rs.${ctx.raw.toLocaleString('en-IN')}` 
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: { callback: (value) => `Rs.${(value / 1000).toFixed(0)}K`, font: { size: 10 } },
                grid: { color: '#e2e8f0' }
            },
            x: {
                ticks: { font: { size: 10 }, rotation: 45 }
            }
        }
    };

    if (loading) {
        return <div className="pl-chart-placeholder small"><FontAwesomeIcon icon={faSpinner} spin /></div>;
    }

    if (!data || data.length === 0) {
        return <div className="pl-chart-placeholder small">{t?.noData}</div>;
    }

    return (
        <div className="pl-chart-container-compact">
            <Bar data={chartData} options={options} height={200} />
        </div>
    );
};

// ==============================================
// Top Expenses List (Compact)
// ==============================================
const PlTopExpenses = ({ data, loading, t }) => {
    const total = data?.reduce((sum, d) => sum + d.total_amount, 0) || 0;
    
    return (
        <div className="pl-top-expenses-compact">
            <div className="pl-section-header-compact">
                <FontAwesomeIcon icon={faChartPie} />
                <span>{t?.topExpenses || 'Top Expenses'}</span>
            </div>
            <div className="pl-expense-list-compact">
                {loading ? (
                    <div className="pl-loading-small"><FontAwesomeIcon icon={faSpinner} spin /></div>
                ) : !data || data.length === 0 ? (
                    <div className="pl-empty-small">{t?.noData}</div>
                ) : (
                    data.slice(0, 4).map((expense, idx) => {
                        const percentage = total > 0 ? (expense.total_amount / total) * 100 : 0;
                        return (
                            <div key={idx} className="pl-expense-item-compact">
                                <div className="pl-expense-info-compact">
                                    <span className="pl-expense-name-compact">{expense.category}</span>
                                    <span className="pl-expense-amount-compact">Rs.{expense.total_amount.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="pl-expense-bar-bg-compact">
                                    <div className="pl-expense-bar-compact" style={{ width: `${percentage}%` }} />
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

// ==============================================
// Dues List Component
// ==============================================
const PlDuesList = ({ title, data, icon, color, loading, t }) => {
    return (
        <div className="pl-dues-card" style={{ borderLeftColor: color }}>
            <div className="pl-dues-header">
                <FontAwesomeIcon icon={icon} />
                <span>{title}</span>
                <span className="pl-dues-total">Rs.{(data?.total_dues || 0).toLocaleString('en-IN')}</span>
            </div>
            <div className="pl-dues-count">{data?.customer_count || data?.supplier_count || 0} {title.includes('Customer') ? 'customers' : 'suppliers'}</div>
            <div className="pl-dues-list">
                {loading ? (
                    <div className="pl-loading-small"><FontAwesomeIcon icon={faSpinner} spin /></div>
                ) : !data?.details || data.details.length === 0 ? (
                    <div className="pl-empty-small">No dues</div>
                ) : (
                    data.details.slice(0, 4).map((due, idx) => (
                        <div key={idx} className="pl-due-item">
                            <span className="pl-due-name">{due.customer_name || due.supplier_name}</span>
                            <span className="pl-due-amount">Rs.{due.amount_due.toLocaleString('en-IN')}</span>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

// ==============================================
// Filter Bar Component
// ==============================================
const PlFilterBar = ({ filter, setFilter, dateRange, setDateRange, onApply, onReset, loading, t }) => {
    const [localFilter, setLocalFilter] = useState(filter);
    const [localStartDate, setLocalStartDate] = useState(dateRange.startDate);
    const [localEndDate, setLocalEndDate] = useState(dateRange.endDate);
    
    const filterOptions = [
        { value: 'today', label: t?.today || 'Today' },
        { value: 'week', label: t?.thisWeek || 'This Week' },
        { value: 'month', label: t?.thisMonth || 'This Month' },
        { value: 'year', label: t?.thisYear || 'This Year' },
        { value: 'custom', label: t?.custom || 'Custom' }
    ];
    
    const handleApply = () => {
        setFilter(localFilter);
        onApply(localFilter, localStartDate, localEndDate);
    };
    
    const handleReset = () => {
        setLocalFilter('month');
        setLocalStartDate('');
        setLocalEndDate('');
        setFilter('month');
        onApply('month', '', '');
    };
    
    return (
        <div className="pl-filter-bar">
            <div className="pl-filter-group">
                <label>{t?.filterByDate || 'Filter by Date'}</label>
                <select value={localFilter} onChange={(e) => setLocalFilter(e.target.value)}>
                    {filterOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>
            
            {localFilter === 'custom' && (
                <>
                    <div className="pl-filter-group">
                        <label>{t?.startDate || 'Start Date'}</label>
                        <input type="date" value={localStartDate} onChange={(e) => setLocalStartDate(e.target.value)} />
                    </div>
                    <div className="pl-filter-group">
                        <label>{t?.endDate || 'End Date'}</label>
                        <input type="date" value={localEndDate} onChange={(e) => setLocalEndDate(e.target.value)} />
                    </div>
                </>
            )}
            
            <div className="pl-filter-actions">
                <button className="pl-btn pl-btn-primary" onClick={handleApply} disabled={loading}>
                    <FontAwesomeIcon icon={faCheckCircle} /> {t?.apply || 'Apply'}
                </button>
                <button className="pl-btn pl-btn-secondary" onClick={handleReset} disabled={loading}>
                    <FontAwesomeIcon icon={faTimes} /> {t?.reset || 'Reset'}
                </button>
            </div>
        </div>
    );
};

// ==============================================
// Main ProfitLossReport Component
// ==============================================
const ProfitLossReport = () => {
    const { user, currentShop } = useAuth();
    
    const [shopId, setShopId] = useState(currentShop?.shop_id || '');
    const [shopName, setShopName] = useState(currentShop?.shop_name || '');
    const [currentLang, setCurrentLang] = useState('en');
    const [t, setT] = useState({});
    
    // Data states
    const [dashboardData, setDashboardData] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    
    // Filter states
    const [filter, setFilter] = useState('month');
    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    
    // Toast state
    const [toast, setToast] = useState({ show: false, type: '', message: '', progress: 100 });
    
    const toastTimerRef = useRef(null);
    
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        setToast({ show: true, type, message, progress: 100 });
        let progress = 100;
        const timer = setInterval(() => {
            progress -= 2;
            setToast(prev => ({ ...prev, progress }));
            if (progress <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 50);
        toastTimerRef.current = timer;
    }, []);
    
    // Load translations
    useEffect(() => {
        const loadT = async () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            setCurrentLang(lang);
            const translations = await translator.getTranslations(lang);
            setT(translations);
        };
        loadT();
        
        const handleStorage = () => loadT();
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);
    
    // Update shop info
    useEffect(() => {
        if (currentShop) {
            setShopId(currentShop.shop_id);
            setShopName(currentShop.shop_name);
        }
    }, [currentShop]);
    
    // Get date range based on filter
    const getDateRange = useCallback((filterType, start, end) => {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth();
        
        switch(filterType) {
            case 'today':
                return { start: today.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
            case 'week':
                const weekStart = new Date(today);
                weekStart.setDate(today.getDate() - today.getDay());
                return { start: weekStart.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
            case 'month':
                return { start: `${year}-${String(month+1).padStart(2,'0')}-01`, end: today.toISOString().split('T')[0] };
            case 'year':
                return { start: `${year}-01-01`, end: today.toISOString().split('T')[0] };
            case 'custom':
                return { start: start || `${year}-01-01`, end: end || today.toISOString().split('T')[0] };
            default:
                return { start: `${year}-${String(month+1).padStart(2,'0')}-01`, end: today.toISOString().split('T')[0] };
        }
    }, []);
    
    // Load dashboard summary
    const loadDashboard = useCallback(async () => {
        if (!shopId) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pos_profitloss.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_dashboard_summary', shop_id: shopId, username: user?.username })
            });
            const data = await response.json();
            if (data.status === 'success') setDashboardData(data.data);
        } catch (error) {
            console.error('Error loading dashboard:', error);
        }
    }, [shopId, user?.username]);
    
    // Load report data
    const loadReport = useCallback(async (filterType, start, end) => {
        if (!shopId) {
            showToast('error', 'Please select a shop first');
            return;
        }
        
        setLoading(true);
        const range = getDateRange(filterType, start, end);
        
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pos_profitloss.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_profit_loss_report',
                    shop_id: shopId,
                    username: user?.username,
                    filter: filterType === 'today' ? 'daily' : (filterType === 'week' ? 'weekly' : (filterType === 'month' ? 'monthly' : (filterType === 'year' ? 'yearly' : 'custom'))),
                    start_date: range.start,
                    end_date: range.end
                })
            });
            const data = await response.json();
            if (data.status === 'success') setReportData(data.data);
        } catch (error) {
            console.error('Error loading report:', error);
            showToast('error', 'Failed to load report');
        } finally {
            setLoading(false);
        }
    }, [shopId, user?.username, getDateRange, showToast]);
    
    // Load chart data
    const loadChartData = useCallback(async (filterType, start, end) => {
        if (!shopId) return;
        
        const range = getDateRange(filterType, start, end);
        
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pos_profitloss.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_chart_data',
                    shop_id: shopId,
                    username: user?.username,
                    filter: filterType === 'today' ? 'daily' : (filterType === 'week' ? 'weekly' : (filterType === 'month' ? 'monthly' : 'yearly')),
                    start_date: range.start,
                    end_date: range.end
                })
            });
            const data = await response.json();
            if (data.status === 'success') setChartData(data.data);
        } catch (error) {
            console.error('Error loading chart data:', error);
        }
    }, [shopId, user?.username, getDateRange]);
    
    // Handle filter apply
    const handleApplyFilters = (filterType, start, end) => {
        setFilter(filterType);
        setDateRange({ startDate: start, endDate: end });
        loadReport(filterType, start, end);
        loadChartData(filterType, start, end);
    };
    
    // Initial load
    useEffect(() => {
        if (shopId) {
            loadDashboard();
            loadReport('month', '', '');
            loadChartData('month', '', '');
        }
    }, [shopId, loadDashboard, loadReport, loadChartData]);
    
    // Export CSV
    const exportCSV = () => {
        if (!reportData) return;
        
        const csvRows = [];
        csvRows.push(['Profit & Loss Report', `Generated: ${new Date().toLocaleString()}`]);
        csvRows.push([`Shop: ${shopName}`, `Period: ${reportData.period?.start_date} to ${reportData.period?.end_date}`]);
        csvRows.push([]);
        csvRows.push(['Metric', 'Amount (Rs.)']);
        csvRows.push(['Total Sales', reportData.sales?.total || 0]);
        csvRows.push(['Cost of Goods Sold', reportData.cost_of_goods_sold || 0]);
        csvRows.push(['Gross Profit', reportData.gross_profit || 0]);
        csvRows.push(['Total Expenses', reportData.expenses?.total || 0]);
        csvRows.push(['Net Profit', reportData.net_profit || 0]);
        
        const csvContent = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `profit_loss_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        showToast('success', 'Report exported successfully');
    };
    
    // Print report
    const printReport = () => window.print();
    
    const isProfit = reportData?.net_profit > 0;
    
    return (
        <div className="pl-report-container">
            <PlToast toast={toast} setToast={setToast} t={t} />
            
            {/* Header */}
            <div className="pl-header">
                <div className="pl-header-left">
                    <div className="pl-header-icon">
                        <FontAwesomeIcon icon={faChartLine} />
                    </div>
                    <div className="pl-header-text">
                        <h1>{t?.profitLoss || 'Profit & Loss Report'}</h1>
                        <p>{t?.financialDashboard || 'Financial Dashboard'}</p>
                    </div>
                </div>
                <div className="pl-header-right">
                    <div className="pl-shop-badge">
                        <FontAwesomeIcon icon={faStore} />
                        <span>{shopName}</span>
                    </div>
                    {/* <div className="pl-header-actions">
                        <button className="pl-icon-btn" onClick={exportCSV} title="Export CSV">
                            <FontAwesomeIcon icon={faDownload} />
                        </button>
                        <button className="pl-icon-btn" onClick={printReport} title="Print">
                            <FontAwesomeIcon icon={faPrint} />
                        </button>
                    </div> */}
                </div>
            </div>
            
            {/* Filter Bar */}
            <PlFilterBar
                filter={filter}
                setFilter={setFilter}
                dateRange={dateRange}
                setDateRange={setDateRange}
                onApply={handleApplyFilters}
                onReset={() => handleApplyFilters('month', '', '')}
                loading={loading}
                t={t}
            />
            
            {/* Metric Cards */}
            <div className="pl-metrics-grid">
                <PlMetricCard
                    title={t?.totalRevenue || 'Total Revenue'}
                    value={reportData?.sales?.total || 0}
                    subtitle={`${reportData?.sales?.transactions || 0} transactions`}
                    change={12.5}
                    icon={faMoneyBillWave}
                    color="#10b981"
                    loading={loading}
                    t={t}
                />
                <PlMetricCard
                    title={t?.totalExpenses || 'Total Expenses'}
                    value={reportData?.expenses?.total || 0}
                    subtitle={`Salaries + Raw Materials + Others`}
                    change={-5.2}
                    icon={faCalculator}
                    color="#ef4444"
                    loading={loading}
                    t={t}
                />
                <PlMetricCard
                    title={t?.grossProfit || 'Gross Profit'}
                    value={reportData?.gross_profit || 0}
                    subtitle={`Margin: ${reportData?.gross_margin_percentage?.toFixed(1) || 0}%`}
                    icon={faChartLine}
                    color="#3b82f6"
                    loading={loading}
                    t={t}
                />
                <PlMetricCard
                    title={t?.netProfit || 'Net Profit'}
                    value={Math.abs(reportData?.net_profit || 0)}
                    subtitle={`Margin: ${reportData?.net_margin_percentage?.toFixed(1) || 0}%`}
                    change={isProfit ? 8.3 : -8.3}
                    icon={faArrowUp}
                    color={isProfit ? '#8b5cf6' : '#ef4444'}
                    loading={loading}
                    t={t}
                />
            </div>
            
            {/* Main Charts Row - Revenue vs Expenses (Large) + Expense Breakdown (Small) */}
            <div className="pl-charts-main-row">
                <div className="pl-chart-card-main">
                    <div className="pl-card-header">
                        <h3>{t?.dailyPerformance || 'Revenue vs Expenses Trend'}</h3>
                    </div>
                    <PlRevenueExpenseChart data={chartData?.daily_trend} loading={loading} t={t} />
                </div>
                <div className="pl-chart-card-side">
                    <div className="pl-card-header">
                        <h3>{t?.expenseBreakdown || 'Expense Breakdown'}</h3>
                    </div>
                    <PlExpensePieChart data={chartData?.expense_pie} loading={loading} t={t} />
                    <PlTopExpenses data={chartData?.expense_pie} loading={loading} t={t} />
                </div>
            </div>
            
            {/* <!-- Monthly Trend + Dues Row --> */}
            <div className="pl-bottom-row">
                <div className="pl-trend-card">
                    <div className="pl-card-header">
                        <h3>{t?.monthlyTrends || 'Monthly Profit Trend'}</h3>
                    </div>
                    <PlProfitTrendChart data={chartData?.monthly_trend} loading={loading} t={t} />
                </div>
                <div className="pl-dues-column">
                    <PlDuesList
                        title={t?.customerReceivables || 'Customer Receivables'}
                        data={reportData?.customer_dues}
                        icon={faUsers}
                        color="#3b82f6"
                        loading={loading}
                        t={t}
                    />
                    <PlDuesList
                        title={t?.supplierPayables || 'Supplier Payables'}
                        data={reportData?.supplier_dues}
                        icon={faTruck}
                        color="#f59e0b"
                        loading={loading}
                        t={t}
                    />
                </div>
            </div>
            
            {/* Expense Detail Section */}
            <div className="pl-detail-section">
                <div className="pl-detail-header">
                    <h3>{t?.expenseBreakdown || 'Expense Details'}</h3>
                </div>
                <div className="pl-expense-detail-grid">
                    <div className="pl-expense-detail-item">
                        <div className="pl-expense-detail-icon salaries">
                            <FontAwesomeIcon icon={faMoneyBillWave} />
                        </div>
                        <div className="pl-expense-detail-info">
                            <div className="pl-expense-detail-label">{t?.salaries || 'Salaries'}</div>
                            <div className="pl-expense-detail-value">Rs.{(reportData?.expenses?.breakdown?.salaries || 0).toLocaleString('en-IN')}</div>
                        </div>
                    </div>
                    <div className="pl-expense-detail-item">
                        <div className="pl-expense-detail-icon raw">
                            <FontAwesomeIcon icon={faIndustry} />
                        </div>
                        <div className="pl-expense-detail-info">
                            <div className="pl-expense-detail-label">{t?.rawMaterials || 'Raw Materials'}</div>
                            <div className="pl-expense-detail-value">Rs.{(reportData?.expenses?.breakdown?.raw_material_usage || 0).toLocaleString('en-IN')}</div>
                        </div>
                    </div>
                    <div className="pl-expense-detail-item">
                        <div className="pl-expense-detail-icon other">
                            <FontAwesomeIcon icon={faReceipt} />
                        </div>
                        <div className="pl-expense-detail-info">
                            <div className="pl-expense-detail-label">{t?.otherExpenses || 'Other Expenses'}</div>
                            <div className="pl-expense-detail-value">Rs.{(reportData?.expenses?.breakdown?.other_expenses || 0).toLocaleString('en-IN')}</div>
                        </div>
                    </div>
                    <div className="pl-expense-detail-item">
                        <div className="pl-expense-detail-icon cogs">
                            <FontAwesomeIcon icon={faBox} />
                        </div>
                        <div className="pl-expense-detail-info">
                            <div className="pl-expense-detail-label">{t?.costOfGoodsSold || 'Cost of Goods Sold'}</div>
                            <div className="pl-expense-detail-value">Rs.{(reportData?.cost_of_goods_sold || 0).toLocaleString('en-IN')}</div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Loading Overlay */}
            {loading && (
                <div className="pl-loading-overlay">
                    <div className="pl-loading-card">
                        <div className="pl-loading-spinner"></div>
                        <p>{t?.loading || 'Loading financial data...'}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfitLossReport;